import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { Button, Box, Text } from '@components/common';
import { Page, LandingContainer } from '@containers/layout';
import Logo from '@components/Logo';
import SignupLink from '@components/SignupLink';
import history from '@utils/history';
import {
  load as loadGoogle,
  login as loginWithGoogle,
  generateUser as generateGoogleUser
} from '@services/google';
import {
  load as loadApple,
  login as loginWithApple,
  generateUser as generateAppleUser
} from '@services/apple';
import {
  load as loadFacebook,
  login as loginWithFacebook,
  generateUser as generateFacebookUser
} from '@services/facebook';

import API from '@utils/api';
import AuthActions from '@redux/AuthRedux';
import AppActions, { LOGIN_PAGES } from '@redux/AppRedux';
import SignupActions from '@redux/SignupRedux';
import { getErrors } from '@utils/error';
import SupportModal from '@containers/account/SupportModal';

const PLUGIN_LOAD_STATUS = {
  NONE: 0,
  LOADING: 1,
  LOADED: 2
};

function LandingPage(props) {
  const [showSupportModal, setShowSupportModal] = useState(false);
  const { socialLogin, socialRegister } = props;
  const [googleLoadStatus, setGoogleLoadStatus] = useState(
    PLUGIN_LOAD_STATUS.NONE
  );

  const [facebookLoadStatus, setFacebookLoadStatus] = useState(
    PLUGIN_LOAD_STATUS.NONE
  );

  const [appleLoadStatus, setAppleLoadStatus] = useState(
    PLUGIN_LOAD_STATUS.NONE
  );

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errMsg, setErrMsg] = useState('');

  const loadGoogleSDK = async () => {
    if (googleLoadStatus === PLUGIN_LOAD_STATUS.NONE) {
      try {
        setGoogleLoadStatus(PLUGIN_LOAD_STATUS.LOADING);
        await loadGoogle();
        setGoogleLoadStatus(PLUGIN_LOAD_STATUS.LOADED);
      } catch (err) {
        console.log('err', err);
      }
    }
  };

  const loadFacebookSDK = async () => {
    if (facebookLoadStatus === PLUGIN_LOAD_STATUS.NONE) {
      try {
        setFacebookLoadStatus(PLUGIN_LOAD_STATUS.LOADING);
        await loadFacebook();
        setFacebookLoadStatus(PLUGIN_LOAD_STATUS.LOADED);
      } catch (err) {
        console.log('err', err);
      }
    }
  };

  const loadAppleSDK = async () => {
    if (appleLoadStatus === PLUGIN_LOAD_STATUS.NONE) {
      try {
        setAppleLoadStatus(PLUGIN_LOAD_STATUS.LOADING);
        await loadApple();
        setAppleLoadStatus(PLUGIN_LOAD_STATUS.LOADED);
      } catch (err) {
        console.log('err', err);
      }
    }
  };

  useEffect(() => {
    loadGoogleSDK();
    loadFacebookSDK();
    loadAppleSDK();
  }, [googleLoadStatus, facebookLoadStatus]);

  const handleSSOResult = async (ssoResult, socialProfile) => {
    const { isNew, authToken, refreshToken, ssoType } = ssoResult;
    if (isNew) {
      // socialRegister(
      //   {
      //     authToken,
      //     refreshToken,
      //     ssoType
      //   },
      //   socialProfile
      // );
      // history.push('/auth/register');
      setShowSupportModal(true);
      return;
    }

    socialLogin({
      authToken,
      refreshToken
    });
  };

  const handleSigninWithApple = async () => {
    setErrMsg('');
    setIsSubmitting(true);
    try {
      const resp = await loginWithApple();
      const { token: appleToken, profile } = generateAppleUser(resp);

      const { idToken: token, type } = appleToken;

      const { result } = await API.loginWithSocial({
        token,
        type
      });

      await handleSSOResult(
        {
          ...result,
          ssoType: 'apple'
        },
        profile
      );
    } catch (err) {
      const error = getErrors(err);
      setErrMsg(error.message);
    }

    setIsSubmitting(false);
  };

  const handleSigninWithGoogle = async () => {
    setErrMsg('');
    setIsSubmitting(true);
    try {
      const resp = await loginWithGoogle();

      const { token: gToken, profile } = generateGoogleUser(resp);
      const { idToken: token, type } = gToken;

      const { result } = await API.loginWithSocial({
        token,
        type
      });

      await handleSSOResult(result, profile);
    } catch (err) {
      const error = getErrors(err);
      setErrMsg(error.message);
    }

    setIsSubmitting(false);
  };

  const handleSigninWithFacebook = async () => {
    setErrMsg('');
    setIsSubmitting(true);
    try {
      const resp = await loginWithFacebook();
      const { token: fbToken, profile } = generateFacebookUser(resp);
      const { accessToken: token, type } = fbToken;

      const { result } = await API.loginWithSocial({
        token,
        type
      });

      await handleSSOResult(result, profile);
    } catch (err) {
      const error = getErrors(err);
      setErrMsg(error.message);
    }

    setIsSubmitting(false);
  };

  return (
    <Page>
      <LandingContainer
        display="flex"
        flexDirection="column"
        justifyContent="center"
      >
        <Box
          mb={4}
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
        >
          <Logo />
        </Box>
        <Button
          mb={4}
          onClick={handleSigninWithFacebook}
          disabled={
            facebookLoadStatus !== PLUGIN_LOAD_STATUS.LOADED || isSubmitting
          }
        >
          Sign in with Facebook
        </Button>
        <Button
          mb={4}
          disabled={
            googleLoadStatus !== PLUGIN_LOAD_STATUS.LOADED || isSubmitting
          }
          onClick={handleSigninWithGoogle}
        >
          Sign in with Google
        </Button>
        <Button
          mb={4}
          disabled={
            appleLoadStatus !== PLUGIN_LOAD_STATUS.LOADED || isSubmitting
          }
          onClick={handleSigninWithApple}
        >
          Sign in with Apple
        </Button>
        <Button
          mb={4}
          onClick={() => {
            history.push('/auth/login');
          }}
        >
          Sign in with Email
        </Button>

        {errMsg && (
          <Text px={2} mb={4} color="red">
            {errMsg}
          </Text>
        )}
        <SignupLink mb={7} />
      </LandingContainer>
      <SupportModal
        link="https://spruce.page.link/47nY"
        show={showSupportModal}
        dismiss={() => {
          setShowSupportModal(false);
        }}
      />
    </Page>
  );
}

LandingPage.propTypes = {
  socialLogin: PropTypes.func,
  socialRegister: PropTypes.func
};

const mapDispatchToProps = dispatch => ({
  socialLogin: token => {
    dispatch(AuthActions.setLoggedIn(token));
    dispatch(AppActions.setAppDataLoadRequest(LOGIN_PAGES['LOG_IN']));
  },
  socialRegister: (token, socialProfile) => {
    dispatch(SignupActions.initSocialSignup(token, socialProfile));
  }
});

export default connect(
  null,
  mapDispatchToProps
)(LandingPage);
